import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { SubscriptionsBookItem } from '@global/components/SubscriptionsBookItem';
import { PurchasedItemData } from '@src/global/components/SubscriptionsBookItem/types';

// GraphQL
import {
  PaybBillingPlanFrequency,
  SubscriptionProduct as Product,
} from '@global/types/types';

interface Props {
  product: Product;
  isActive?: boolean;
  isCancelled?: boolean;
  totalPrice: string;
  periodInfo?: {
    subscriptionBegan: string;
    nextBillDate: string;
    subscriptionEndDate: string;
  };
  subscriptionFrequency: PaybBillingPlanFrequency;
  accessSubscriptionButton: React.ReactNode;
}

export const SubscriptionProduct: React.FC<Props> = ({
  product,
  isActive,
  isCancelled,
  periodInfo,
  totalPrice,
  subscriptionFrequency,
  accessSubscriptionButton,
}) => {
  const { t } = useTranslation();

  const topLeft = useMemo(
    () =>
      isCancelled
        ? ''
        : isActive
        ? t('subDetails.sub.details.nextBill')
        : t('subDetails.sub.details.endDate'),
    [isCancelled, isActive, t]
  );

  const bottLeft = useMemo(
    () =>
      isCancelled
        ? ''
        : isActive
        ? periodInfo.nextBillDate
        : periodInfo.subscriptionEndDate,
    [isCancelled, isActive, periodInfo]
  );

  const frequency =
    t(`subscription.${subscriptionFrequency.ofUnit}.capitalizedFrequency`) ||
    '';

  const itemData: PurchasedItemData = useMemo(
    () => ({
      imgUrl: product.imageUrl,
      button: accessSubscriptionButton,
      navEntity: {
        to: 'active-subscriptions',
        isInAppNav: true,
        label: product.maName,
      },
      bottomContent: {
        top: {
          left: topLeft,
          right: isActive
            ? t('subDetails.sub.details.recurringBill', { frequency })
            : '',
        },
        bottom: {
          left: bottLeft,
          right: isActive ? totalPrice : '',
        },
      },
    }),
    [product, topLeft, bottLeft, isActive, totalPrice, t]
  );

  return <SubscriptionsBookItem itemEntity={itemData} active={isActive} />;
};
